import type { PositionType } from '../types';

import {
    isStopLossReachable,
    isTakeProfitReachable,
    roiPercentToPriceBps,
} from './pnl';

/**
 * A TP or SL setting, as armed on the order form.
 *
 * The presets are ROI on collateral ("+100%"), which is the right unit for
 * "how much am I risking" but the wrong one for "get me out at 80,435" —
 * reported by aRtik, who could set a percentage and nothing else. The
 * post-open editor has always taken a price; the order form now does too.
 *
 * Stored as one string so nothing else in the store had to change:
 *
 *   'Off'       no trigger
 *   '100%'      ROI on collateral, the existing presets
 *   '80435.3'   an absolute price
 *
 * The `%` suffix is the discriminator, which every previously-persisted value
 * already carries.
 */
export type TriggerSetting =
    | { kind: 'off' }
    | { kind: 'roi'; percent: number }
    | { kind: 'price'; price: number };

export const TRIGGER_OFF = 'Off';

export function parseTriggerSetting(
    raw: string | null | undefined,
): TriggerSetting {
    const text = (raw ?? '').trim();
    if (!text || text.toLowerCase() === 'off') return { kind: 'off' };

    if (text.endsWith('%')) {
        const percent = Number.parseFloat(text.slice(0, -1));
        return Number.isFinite(percent) && percent > 0
            ? { kind: 'roi', percent }
            : { kind: 'off' };
    }

    const price = Number.parseFloat(text);
    return Number.isFinite(price) && price > 0
        ? { kind: 'price', price }
        : { kind: 'off' };
}

/** Store form of an absolute price. Kept in one place so the `%`-free
 *  convention is never re-invented at a call site. */
export function priceSettingValue(price: number | string): string {
    return String(price);
}

export function isPriceSetting(raw: string | null | undefined): boolean {
    return parseTriggerSetting(raw).kind === 'price';
}

/**
 * The trigger price this setting implies, for the chart's mark lines and the
 * order preview. A price setting IS the answer; an ROI setting has to be
 * converted against the entry the order is expected to fill at.
 */
export function triggerPriceFrom(
    raw: string | null | undefined,
    entryPrice: number,
    leverage: number,
    positionType: PositionType,
    isTakeProfit: boolean,
): number | null {
    const setting = parseTriggerSetting(raw);
    if (setting.kind === 'off') return null;
    if (setting.kind === 'price') return setting.price;

    if (!Number.isFinite(entryPrice) || entryPrice <= 0) return null;
    const bps = roiPercentToPriceBps(setting.percent, leverage);
    if (bps <= 0) return null;

    // a take profit sits on the winning side, a stop loss on the losing one
    const towardProfit = isTakeProfit === (positionType === 'LONG') ? 1 : -1;
    // below-mark legs run out of price at a 100% move: no line at $0
    if (!isTriggerReachable(raw, leverage, positionType, isTakeProfit)) {
        return null;
    }
    return entryPrice * (1 + towardProfit * (bps / 10_000));
}

/**
 * Whether the armed setting can ever fire for this side at this leverage.
 *
 * The ROI presets persist across markets, so a 300% take profit armed on BTC
 * at 30x (a 10% price move) becomes a 100% price move on a 3x pair. A short
 * cannot gain that: the price would have to reach $0, and the backend rejects
 * the trigger. A typed price is the user's own number; the backend checks its
 * side and rounding, so it counts as reachable here.
 */
export function isTriggerReachable(
    raw: string | null | undefined,
    leverage: number,
    positionType: PositionType,
    isTakeProfit: boolean,
    maintenanceRate = 0,
): boolean {
    const setting = parseTriggerSetting(raw);
    if (setting.kind !== 'roi') return true;
    return isTakeProfit
        ? isTakeProfitReachable(setting.percent, leverage, positionType)
        : isStopLossReachable(
              setting.percent,
              leverage,
              positionType,
              maintenanceRate,
          );
}

/**
 * The caption under the take-profit presets naming the ones a short cannot
 * reach at this leverage, e.g. "At 3x, 300% take profit is long only." Null
 * when every preset works both ways, which is every market above 3x.
 */
export function longOnlyTakeProfitHint(
    options: readonly string[],
    leverage: number,
): string | null {
    const longOnly = options.filter(
        (opt) =>
            parseTriggerSetting(opt).kind === 'roi' &&
            !isTriggerReachable(opt, leverage, 'SHORT', true),
    );
    if (longOnly.length === 0) return null;
    return `At ${leverage}x, ${longOnly.join(' and ')} take profit is long only.`;
}

/**
 * The caption under the stop-loss presets naming the ones that sit AT or BEYOND
 * the liquidation price at this leverage, e.g. "At 3x, a 50% stop loss would
 * liquidate first." Such a stop reads as armed protection and can never fire —
 * the position is force-closed before the price ever reaches it, which is how
 * the reported PONS short was liquidated with a stop loss set.
 *
 * Side-aware, because the maintenance margin makes a SHORT liquidate on a
 * smaller move than a LONG at the same leverage: whatever is unreachable on a
 * long is unreachable on a short too, so the short-only cases are named as
 * such and the rest are named plainly. Null while the market's margin terms
 * have not loaded and nothing is affected, which is every market above 3x.
 */
export function liquidatesFirstStopLossHint(
    options: readonly string[],
    leverage: number,
    maintenanceRate = 0,
): string | null {
    const roiOptions = options.filter(
        (opt) => parseTriggerSetting(opt).kind === 'roi',
    );
    const unreachableOn = (side: PositionType) =>
        roiOptions.filter(
            (opt) =>
                !isTriggerReachable(
                    opt,
                    leverage,
                    side,
                    false,
                    maintenanceRate,
                ),
        );
    const onLong = unreachableOn('LONG');
    const onShort = unreachableOn('SHORT');
    if (onShort.length === 0) return null;

    const sentences: string[] = [];
    if (onLong.length > 0) {
        sentences.push(
            `At ${leverage}x, a ${onLong.join(' and ')} stop loss would liquidate first.`,
        );
    }
    const shortOnly = onShort.filter((opt) => !onLong.includes(opt));
    if (shortOnly.length > 0) {
        sentences.push(
            `At ${leverage}x, a ${shortOnly.join(' and ')} stop loss would liquidate first on a short.`,
        );
    }
    return sentences.join(' ');
}

/**
 * What to put on the wire for `POST /speed/trade/open`.
 *
 * The backend accepts either an absolute price or a bps offset from mark, and
 * the explicit price wins — so this sends exactly one of them. It validates the
 * side and rounds to Hyperliquid's tick rules itself, which is why a price goes
 * over verbatim rather than being pre-rounded here against a mark this form does
 * not authoritatively know.
 */
export function triggerRequestFields(
    raw: string | null | undefined,
    leverage: number,
    field: 'takeProfit' | 'stopLoss',
): Record<string, string | number> {
    const setting = parseTriggerSetting(raw);
    if (setting.kind === 'off') return {};
    if (setting.kind === 'price') return { [field]: String(setting.price) };

    const bps = roiPercentToPriceBps(setting.percent, leverage);
    return bps > 0 ? { [`${field}Bps`]: bps } : {};
}

/** Short form for the collapsed summary row: `100%` or `$80,435.30`. */
export function triggerSummaryLabel(
    raw: string | null | undefined,
    formatPrice: (value: number) => string,
): string {
    const setting = parseTriggerSetting(raw);
    if (setting.kind === 'off') return TRIGGER_OFF;
    if (setting.kind === 'roi') return `${setting.percent}%`;
    return `$${formatPrice(setting.price)}`;
}
